"use client";

import React, { useEffect, useRef, useState } from "react";
import { GoBack } from "~/components/buttons/go-back";
import { BlurImage } from "~/components/images/blur-image";
import { Campaign } from "~/components/icons/campaign";
import { CircleChecked } from "~/components/icons/CircleChecked";
import { GolfCourse } from "~/components/icons/golf-course";
import { PlaylistAddCheck } from "~/components/icons/playlist-add-check";
import { Timer } from "~/components/icons/timer";
import { Crosshair, Tag } from "lucide-react";
import Waitlists from "~/components/waitlist-page/waitlists";
import { NameYourPriceForm, type NypOfferDetails } from "~/components/waitlist-page/name-your-price-form";
import { NameYourPricePaymentStep } from "~/components/waitlist-page/name-your-price-payment-step";
import { YourOffers } from "~/components/waitlist-page/your-offers";
import { useAppContext } from "~/contexts/AppContext";
import { useCourseContext } from "~/contexts/CourseContext";
import { useMe } from "~/hooks/useMe";
import { api } from "~/utils/api";
import { useRouter } from "next/navigation";
import { toast } from "react-toastify";
import { InlineAlert } from "~/components/inline-alert/inline-alert";
import { useMediaQuery } from "usehooks-ts";

function NotifyMe({ params }: { params: { course: string } }) {
  const router = useRouter();
  const { user, isLoading } = useMe();
  const { course } = useCourseContext();
  const courseId = params.course;
  const { setActivePage, setPrevPath } = useAppContext();
  const hasSetPrevPath = useRef(false);
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Whether NameYourPrice is offered at all is purely the course-level flag — no
  // separate app-wide test flag anymore (see ENG-1897 #43).
  const showNameYourPrice = !!course?.supportsUnsolicitedOffers;

  // Single combined form: the checkbox inside NameYourPriceForm toggles between
  // submitting a priced offer and joining the plain waitlist. When the flag is off,
  // there's no choice — the form is always in waitlist mode. Synced (not just forced
  // one-way) because the flag query defaults to false while loading — without the
  // sync running in both directions, the mode gets stuck in waitlist once the flag
  // resolves true, instead of defaulting back to NameYourPrice being unchecked.
  // When the course doesn't support the waitlist at all, the mode toggle is hidden
  // (see NameYourPriceForm), so isWaitlistMode must never be forced/stuck true there —
  // otherwise the buyer would be stranded on a waitlist-mode form with no way back.
  const [isWaitlistMode, setIsWaitlistMode] = useState(!showNameYourPrice);
  useEffect(() => {
    setIsWaitlistMode(course?.supportsWaitlist ? !showNameYourPrice : false);
  }, [showNameYourPrice, course?.supportsWaitlist]);

  // Bottom section tab — only relevant/shown when the user actually has entries in
  // both lists; otherwise the single non-empty list is shown directly with no tab
  // switcher. Same queryKey/input as YourOffers and Waitlists themselves, so this
  // shares their React Query cache rather than firing an extra network request.
  const [bottomTab, setBottomTab] = useState<"offers" | "waitlist">("offers");
  const { data: offersForTabCheck } = api.nameYourPriceOffer.getMyOffers.useQuery(
    {},
    { enabled: !!user?.id && showNameYourPrice }
  );
  const { data: waitlistForTabCheck } = api.userWaitlist.getWaitlist.useQuery(
    { courseId },
    { enabled: !!user?.id && !!course?.supportsWaitlist }
  );
  const hasOffers = (offersForTabCheck?.data?.length ?? 0) > 0;
  const hasWaitlistEntries = (waitlistForTabCheck?.length ?? 0) > 0;
  const showOffersTab = showNameYourPrice && hasOffers;
  const showWaitlistTab = !!course?.supportsWaitlist && hasWaitlistEntries;

  const [nypStep, setNypStep] = useState<"form" | "payment">("form");
  const [pendingOffer, setPendingOffer] = useState<NypOfferDetails | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const { mutateAsync: createOfferFromWallet } = api.nameYourPriceOffer.createOffer.useMutation();
  const { refetch: refetchOffersAfterWallet } = api.nameYourPriceOffer.getMyOffers.useQuery({}, { enabled: false });

  // Handle GPay/Apple Pay redirect return — fires at page level so it runs regardless of nypStep
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (!params.get("nyp_return")) return;

    const raw = localStorage.getItem("nyp_pending_offer");
    if (!raw) return;

    try {
      const pending = JSON.parse(raw) as {
        offerDetails: NypOfferDetails;
        paymentId: string;
        holdAmount: number;
      };

      const returnedPaymentId =
        params.get("payment_id") ?? params.get("payment_intent") ?? pending.paymentId;

      localStorage.removeItem("nyp_pending_offer");
      params.delete("nyp_return");
      const cleanUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;
      window.history.replaceState({}, "", cleanUrl);

      createOfferFromWallet({
        courseId: pending.offerDetails.courseId,
        date: pending.offerDetails.date,
        startTime: pending.offerDetails.startTime,
        endTime: pending.offerDetails.endTime,
        players: pending.offerDetails.players,
        pricePerPlayer: pending.offerDetails.pricePerPlayer,
        preAuthId: returnedPaymentId,
        holdAmount: pending.holdAmount,
        redirectHref: window.location.origin,
      }).then(() => {
        toast.success("Your offer has been sent!", {
          progressStyle: { background: "var(--primary-color)" },
        });
        void refetchOffersAfterWallet();
      }).catch(() => {
        setPageError("Offer submission failed after payment. Please contact support.");
      });
    } catch (e) {
      console.error("[NyP] Failed to process wallet return:", e);
      localStorage.removeItem("nyp_pending_offer");
    }

  }, []);

  // Auth redirect
  useEffect(() => {
    if (isLoading) return;
    if (!user) {
      if (!hasSetPrevPath.current) {
        hasSetPrevPath.current = true;
        setPrevPath({
          path: `/${courseId}/notify-me`,
          createdAt: new Date().toISOString(),
        });
      }
      router.replace(`/${courseId}/login`);
    }
  }, [user, isLoading, courseId, setPrevPath, router]);

  useEffect(() => {
    setActivePage("notify-me");
  }, [setActivePage]);

  // This page now hosts both the waitlist and NameYourPrice — only bounce back to the
  // course page when NEITHER is available, not just because waitlist alone is off.
  // Both flags live on the same course object, so there's no separate flag-loading
  // race to wait out here — `!course` alone covers "still loading".
  if (!course || (!course.supportsWaitlist && !showNameYourPrice)) {
    router.push(`/${courseId}`);
  }

  return (
    <section className="mx-auto px-2 flex w-full flex-col gap-4 pt-4 md:max-w-[85rem] md:px-6">
      <div className="flex items-center justify-between px-4 md:px-6">
        <GoBack href={`/${courseId}`} text="Back to tee times" />
      </div>

      {pageError && (
        <div className="px-4 md:px-6">
          <InlineAlert status="error">{pageError}</InlineAlert>
        </div>
      )}

      <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-8 gap-4">
        {/* ── Left column: How it works — switches based on the form's mode ── */}
        <div className="col-span-3 flex flex-col items-start pl-4 md:px-6">
          {showNameYourPrice && !isWaitlistMode ? (
            /* Name Your Price left panel */
            <>
              <h1 className="flex items-center gap-1.5 text-[1.25rem] font-semibold text-secondary-black md:text-[2rem]">
                <span>How</span>
                <BlurImage
                  alt="golf district logo"
                  src={`https://${process.env.NEXT_PUBLIC_AWS_CLOUDFRONT_URL}/mobileheaderlogo.png`}
                  width={isMobile ? 20 : 32}
                  height={isMobile ? 20 : 32}
                  // The source PNG has a white backing plate baked in — blend it
                  // away against this panel's white background instead of
                  // showing it as a visible square chip next to plain text.
                  className="mix-blend-multiply"
                />
                <span>PricePoint works</span>
              </h1>
              <p className="mt-1 text-justify text-[0.8125rem] md:text-[0.9375rem] text-primary-gray">
                Can’t find a tee time that works? Make an offer instead. Tell us the day and time range you want to play and the price you’re willing to pay.
                We send your offer to everyone who could make it happen, and the first one to accept locks in your booking.
              </p>

              <div className="mt-6 w-full">
                <div className="flex items-start mb-6">
                  <div className="flex-shrink-0 mr-4">
                    <Crosshair
                      size={isMobile ? 25 : 30}
                      color="var(--primary-color)"
                    />
                  </div>
                  <div>
                    <h2 className="text-[0.875rem] md:text-[1.125rem] font-semibold">
                      Set your target window
                    </h2>
                    <p className="text-justify text-[0.75rem] md:text-[1rem] text-gray-600">
                      Pick the date and a time range that works for you, like Saturday between 8am and 11am.
                      You don’t need to pick an exact tee time. Your offer goes out to golfers who already hold tee times in that window, plus the course itself.
                    </p>
                  </div>
                </div>
                <hr className="mb-6 border-gray-200" />

                <div className="flex items-start mb-6">
                  <div className="flex-shrink-0 mr-4">
                    <Tag
                      size={isMobile ? 25 : 30}
                      color="var(--primary-color)"
                    />
                  </div>
                  <div>
                    <h2 className="text-[0.875rem] md:text-[1.125rem] font-semibold">
                      Make your offer
                    </h2>
                    <p className="text-justify text-[0.75rem] md:text-[1rem] text-gray-600">
                      Enter the price per player you’re willing to pay. Stronger offers get accepted faster.
                    </p>
                  </div>
                </div>
                <hr className="mb-6 border-gray-200" />

                <div className="flex items-start mb-6">
                  <div className="flex-shrink-0 mr-4">
                    <Campaign
                      width={isMobile ? "1.5625rem" : "1.875rem"}
                      fill="var(--primary-color)"
                    />
                  </div>
                  <div>
                    <h2 className="text-[0.875rem] md:text-[1.125rem] font-semibold">
                      Owners get notified instantly
                    </h2>
                    <p className="text-justify text-[0.75rem] md:text-[1rem] text-gray-600">
                      Everyone holding a matching tee time gets your offer right away. They can accept it, counter with a different price, or pass. The course can even open up a new time just for you.
                    </p>
                  </div>
                </div>
                <hr className="mb-6 border-gray-200" />

                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-4">
                    <CircleChecked
                      width={isMobile ? "1.5625rem" : "1.875rem"}
                      fill="var(--primary-color)"
                    />
                  </div>
                  <div>
                    <h2 className="text-[0.875rem] md:text-[1.125rem] font-semibold">
                      First to accept wins
                    </h2>
                    <p className="text-justify text-[0.75rem] md:text-[1rem] text-gray-600">
                      The first person to accept your offer confirms your booking automatically. Everything else is cancelled and you’re set to play.
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* Waitlist left panel */
            <>
              <h1 className="text-[1.25rem] font-semibold text-secondary-black md:text-[2rem]">
                How the waitlist works
              </h1>
              <p className="mt-1 text-[0.8125rem] md:text-[0.9375rem] text-primary-gray">
                Set your preferred window and get alerted the instant a
                matching tee time opens up.
              </p>

              <div className="mt-6 w-full">
                <div className="flex items-start mb-6">
                  <div className="flex-shrink-0 mr-4">
                    <PlaylistAddCheck
                      width={isMobile ? "1.5625rem" : "1.875rem"}
                      fill="var(--primary-color)"
                    />
                  </div>
                  <div>
                    <h2 className="text-[0.875rem] md:text-[1.125rem] font-semibold">
                      Set your preferences
                    </h2>
                    <p className="text-[0.75rem] md:text-[1rem] text-gray-600">
                      Choose your preferred window and we will monitor it
                      continuously for you.
                    </p>
                  </div>
                </div>
                <hr className="mb-6 border-gray-200" />

                <div className="flex items-start mb-6">
                  <div className="flex-shrink-0 mr-4">
                    <Campaign
                      width={isMobile ? "1.5625rem" : "1.875rem"}
                      fill="var(--primary-color)"
                    />
                  </div>
                  <div>
                    <h2 className="text-[0.875rem] md:text-[1.125rem] font-semibold">
                      Get alerted instantly
                    </h2>
                    <p className="text-[0.75rem] md:text-[1rem] text-gray-600">
                      The moment a tee time opens in your window, you are the
                      first to know.
                    </p>
                  </div>
                </div>
                <hr className="mb-6 border-gray-200" />

                <div className="flex items-start mb-6">
                  <div className="flex-shrink-0 mr-4">
                    <Timer
                      width={isMobile ? "1.5625rem" : "1.875rem"}
                      fill="var(--primary-color)"
                    />
                  </div>
                  <div>
                    <h2 className="text-[0.875rem] md:text-[1.125rem] font-semibold">
                      Book in seconds
                    </h2>
                    <p className="text-[0.75rem] md:text-[1rem] text-gray-600">
                      Act fast to lock in your spot before someone else does.
                    </p>
                  </div>
                </div>
                <hr className="mb-6 border-gray-200" />

                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-4">
                    <GolfCourse
                      width={isMobile ? "1.5625rem" : "1.875rem"}
                      fill="var(--primary-color)"
                    />
                  </div>
                  <div>
                    <h2 className="text-[0.875rem] md:text-[1.125rem] font-semibold">
                      Enjoy your round
                    </h2>
                    <p className="text-[0.75rem] md:text-[1rem] text-gray-600">
                      Confirm your booking, hit the greens, and make the most
                      of your day.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Right column: combined form card ── */}
        <div className="col-span-5 flex flex-col bg-white rounded-xl shadow overflow-hidden">
          {nypStep === "payment" && pendingOffer ? (
            <NameYourPricePaymentStep
              offerDetails={pendingOffer}
              courseName={course?.name ?? ""}
              onBack={() => setNypStep("form")}
              onOfferSent={() => {
                setNypStep("form");
                setPendingOffer(null);
              }}
              onInitError={setPageError}
            />
          ) : (
            <NameYourPriceForm
              hideWrapper
              courseId={courseId}
              initialDetails={pendingOffer}
              onProceedToPayment={(details) => {
                setPendingOffer(details);
                setNypStep("payment");
              }}
              isWaitlistMode={isWaitlistMode}
              onWaitlistModeChange={setIsWaitlistMode}
              forceWaitlistOnly={!showNameYourPrice}
            />
          )}
        </div>
      </div>

      <hr className="mt-4" />

      {/* Bottom section — your sent offers and your waitlist alerts. Tab switcher only
          shows when the user actually has entries in both; otherwise the single
          non-empty list is shown directly with no tab to switch to. When neither has
          any entries yet, fall back to whichever capability the course has enabled
          so there's still something (an empty state) to land on. */}
      {showOffersTab && showWaitlistTab ? (
        <>
          <div className="flex border-b border-gray-200 px-4 md:px-6">
            <button
              onClick={() => setBottomTab("offers")}
              className={`pb-3 px-1 mr-6 text-base md:text-xl border-b-2 -mb-px transition-colors ${bottomTab === "offers"
                ? "border-[var(--primary-color)] text-[var(--primary-color)]"
                : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
            >
              Offers
            </button>
            <button
              onClick={() => setBottomTab("waitlist")}
              className={`pb-3 px-1 text-base md:text-xl border-b-2 -mb-px transition-colors ${bottomTab === "waitlist"
                ? "border-[var(--primary-color)] text-[var(--primary-color)]"
                : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
            >
              Waitlist
            </button>
          </div>
          {bottomTab === "offers" ? (
            <div className="px-4 md:px-6">
              <YourOffers />
            </div>
          ) : (
            <Waitlists />
          )}
        </>
      ) : showOffersTab ? (
        <div className="px-4 md:px-6">
          <YourOffers />
        </div>
      ) : showWaitlistTab ? (
        <Waitlists />
      ) : showNameYourPrice ? (
        <div className="px-4 md:px-6">
          <YourOffers />
        </div>
      ) : (
        <Waitlists />
      )}
    </section>
  );
}

export default NotifyMe;
